import React, { useState, useEffect, useRef, useCallback } from 'react'
import { apiService } from '@/services/api'
import { useAuthStore } from '@/stores/authStore'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Badge } from '@/components/ui/badge'
import { Switch } from '@/components/ui/switch'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'
import {
  Users,
  ShieldCheck,
  Plus,
  Loader2,
  AlertCircle,
  ChevronDown,
  Check,
  KeyRound,
  Eye,
  Pencil,
  Crown,
  Building2,
} from 'lucide-react'
import PageHeader from '@/components/PageHeader'

// Privilege ids per backend `privileges` table
const PRIVILEGES = [
  { id: 1, label: 'Read',  icon: Eye },
  { id: 2, label: 'Write', icon: Pencil },
  { id: 3, label: 'Admin', icon: Crown },
]

// ─── Constants ────────────────────────────────────────────────────────────────

const ROLE_CODES = [
  'CONSULTANT',
  'CLIENT',
  'FOODMANAGER',
  'EXERCISEMANAGER',
  'TRAINER',
  'FOODMANAGERADMIN',
  'EXERCISEMANAGERADMIN',
  'CONSULTANTADMIN',
]

const ROLE_CARD_COLORS = ['blue', 'emerald', 'amber', 'pink', 'indigo', 'purple']

const ROLE_PALETTE = {
  blue:    { icon: 'text-blue-600 bg-blue-100',       badge: 'bg-blue-50 text-blue-700 border-blue-200' },
  emerald: { icon: 'text-emerald-600 bg-emerald-100', badge: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  amber:   { icon: 'text-amber-600 bg-amber-100',     badge: 'bg-amber-50 text-amber-700 border-amber-200' },
  pink:    { icon: 'text-pink-600 bg-pink-100',       badge: 'bg-pink-50 text-pink-700 border-pink-200' },
  indigo:  { icon: 'text-indigo-600 bg-indigo-100',   badge: 'bg-indigo-50 text-indigo-700 border-indigo-200' },
  purple:  { icon: 'text-purple-600 bg-purple-100',   badge: 'bg-purple-50 text-purple-700 border-purple-200' },
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function PermissionsMultiSelect({ permissionItems, selectedIds, onChange, disabled }) {
  const [open, setOpen] = useState(false)
  const containerRef = useRef(null)

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const toggleId = (id) => {
    onChange(
      selectedIds.includes(id)
        ? selectedIds.filter(x => x !== id)
        : [...selectedIds, id]
    )
  }

  const summary = selectedIds.length === 0
    ? 'Select permissions…'
    : `${selectedIds.length} permission${selectedIds.length > 1 ? 's' : ''} selected`

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between rounded-xl border border-input bg-background px-3 py-2 text-sm disabled:cursor-not-allowed disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <span className={selectedIds.length === 0 ? 'text-muted-foreground' : 'text-slate-700'}>
          {summary}
        </span>
        <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute z-50 mt-1.5 w-full max-h-64 overflow-y-auto rounded-xl border border-slate-200 bg-white shadow-lg p-1.5 space-y-0.5">
          {permissionItems.length === 0 ? (
            <p className="text-xs text-slate-400 px-2 py-3 text-center">No permissions available</p>
          ) : (
            permissionItems.map(item => {
              const checked = selectedIds.includes(item.id)
              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => toggleId(item.id)}
                  className={`w-full flex items-start gap-2.5 rounded-lg px-2.5 py-2 text-left transition-colors ${
                    checked ? 'bg-fitness-primary/5' : 'hover:bg-slate-50'
                  }`}
                >
                  <div
                    className={`mt-0.5 h-4 w-4 flex-shrink-0 rounded-md flex items-center justify-center border ${
                      checked ? 'bg-fitness-primary border-fitness-primary' : 'border-slate-300 bg-white'
                    }`}
                  >
                    {checked && <Check className="h-3 w-3 text-white" strokeWidth={3} />}
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-slate-700 leading-tight">{item.display_name}</p>
                    {item.description && (
                      <p className="text-xs text-slate-400 leading-snug mt-0.5">{item.description}</p>
                    )}
                  </div>
                </button>
              )
            })
          )}
        </div>
      )}
    </div>
  )
}

function RoleCard({ role, colorKey, permissionItemsById, onClick }) {
  const palette = ROLE_PALETTE[colorKey]
  const permissionEntries = role.permission_roles || []
  const permissionChips = permissionEntries
    .map(pr => ({
      id: pr.permission_id,
      name: pr.permissions?.[0]?.display_name || permissionItemsById[pr.permission_id]?.display_name,
    }))
    .filter(p => Boolean(p.name))
  const visibleChips = permissionChips.slice(0, 5)
  const remainingCount = permissionChips.length - visibleChips.length

  return (
    <Card
      onClick={onClick}
      className="shadow-sm border-slate-200 cursor-pointer transition-all hover:shadow-md hover:border-slate-300"
    >
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className={`p-2.5 rounded-xl flex-shrink-0 ${palette.icon}`}>
              <ShieldCheck className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <h3 className="font-semibold text-slate-800 truncate">{role.display_name || role.name}</h3>
              {role.role_code && (
                <span className={`inline-block mt-1 text-[11px] font-mono font-medium px-2 py-0.5 rounded-full border ${palette.badge}`}>
                  {role.role_code}
                </span>
              )}
            </div>
          </div>
        </div>

        {role.description && (
          <p className="text-sm text-slate-500 leading-relaxed mb-3 line-clamp-2">{role.description}</p>
        )}

        <div className="flex items-center gap-1.5 text-xs text-slate-400 font-medium mb-2">
          <KeyRound className="h-3.5 w-3.5" />
          {permissionChips.length} permission{permissionChips.length !== 1 ? 's' : ''}
        </div>

        {permissionChips.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {visibleChips.map(chip => (
              <Badge key={chip.id} variant="outline" className="text-[11px] font-normal text-slate-600 border-slate-200 bg-slate-50">
                {chip.name}
              </Badge>
            ))}
            {remainingCount > 0 && (
              <Badge variant="outline" className="text-[11px] font-normal text-slate-500 border-slate-200 bg-slate-50">
                +{remainingCount} more
              </Badge>
            )}
          </div>
        )}

        <p className="text-xs text-fitness-primary font-medium mt-4 pt-3 border-t border-slate-100">
          Click to edit permissions →
        </p>
      </CardContent>
    </Card>
  )
}

// ─── Main Page ────────────────────────────────────────────────────────────────

const RolesEditor = () => {
  const { companyUrl, user } = useAuthStore()
  const isSuperAdmin = user?.role?.id === 1 || user?.role_users?.[0]?.role_id === 1

  const [roles, setRoles] = useState([])
  const [permissionItems, setPermissionItems] = useState([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(null)

  const [companies, setCompanies] = useState([])
  const [selectedCompanyId, setSelectedCompanyId] = useState(null)
  const [loadingCompanies, setLoadingCompanies] = useState(false)

  const [showAddDialog, setShowAddDialog] = useState(false)
  const [newRoleName, setNewRoleName] = useState('')
  const [newRoleDescription, setNewRoleDescription] = useState('')
  const [newRoleCode, setNewRoleCode] = useState('')
  const [newRolePermissionIds, setNewRolePermissionIds] = useState([])
  const [addRoleSaving, setAddRoleSaving] = useState(false)
  const [addRoleError, setAddRoleError] = useState(null)

  const permissionItemsById = React.useMemo(
    () => Object.fromEntries(permissionItems.map(p => [p.id, p])),
    [permissionItems]
  )

  const loadData = useCallback(async () => {
    setLoading(true)
    setLoadError(null)
    const [rolesResult, permissionsResult] = await Promise.all([
      apiService.fetchRoles(companyUrl, isSuperAdmin ? selectedCompanyId : null),
      apiService.fetchPermissionItems(companyUrl),
    ])

    if (!rolesResult.success) {
      setLoadError(rolesResult.error || 'Failed to load roles.')
      setLoading(false)
      return
    }
    if (!permissionsResult.success) {
      setLoadError(permissionsResult.error || 'Failed to load permissions.')
      setLoading(false)
      return
    }

    setRoles(Array.isArray(rolesResult.data) ? rolesResult.data : [])
    setPermissionItems(Array.isArray(permissionsResult.data) ? permissionsResult.data : [])
    setLoading(false)
  }, [companyUrl, isSuperAdmin, selectedCompanyId])

  // Super admin: load all companies and default-select the first one
  useEffect(() => {
    if (!isSuperAdmin || !companyUrl) return
    let cancelled = false
    setLoadingCompanies(true)
    apiService.fetchCompanies(companyUrl).then(result => {
      if (cancelled) return
      if (result.success) {
        const list = Array.isArray(result.data) ? result.data : []
        setCompanies(list)
        if (list.length > 0) setSelectedCompanyId(list[0].id)
      }
      setLoadingCompanies(false)
    })
    return () => { cancelled = true }
  }, [isSuperAdmin, companyUrl])

  useEffect(() => {
    if (!companyUrl) return
    if (isSuperAdmin && !selectedCompanyId) return
    loadData()
  }, [companyUrl, isSuperAdmin, selectedCompanyId, loadData])

  const handleCompanyChange = (value) => {
    setSelectedCompanyId(Number(value))
  }

  const resetAddRoleForm = () => {
    setNewRoleName('')
    setNewRoleDescription('')
    setNewRoleCode('')
    setNewRolePermissionIds([])
    setAddRoleError(null)
  }

  const handleOpenAddDialog = () => {
    resetAddRoleForm()
    setShowAddDialog(true)
  }

  const handleAddRole = async () => {
    if (!newRoleName.trim()) {
      setAddRoleError('Please enter a role name.')
      return
    }

    setAddRoleSaving(true)
    setAddRoleError(null)

    const result = await apiService.createRole(companyUrl, {
      name: newRoleName.trim(),
      description: newRoleDescription.trim() || undefined,
      role_code: newRoleCode || undefined,
      permissions: newRolePermissionIds,
      company_id: isSuperAdmin ? selectedCompanyId : undefined,
    })

    if (!result.success) {
      setAddRoleError(result.error || 'Failed to create role.')
      setAddRoleSaving(false)
      return
    }

    setAddRoleSaving(false)
    setShowAddDialog(false)
    resetAddRoleForm()
    await loadData()
  }

  // ── Edit role permissions ────────────────────────────────────────────────
  const [showEditDialog, setShowEditDialog] = useState(false)
  const [editingRole, setEditingRole] = useState(null)
  const [editPermissions, setEditPermissions] = useState([])
  const [editLoading, setEditLoading] = useState(false)
  const [editLoadError, setEditLoadError] = useState(null)
  const [togglingKeys, setTogglingKeys] = useState(new Set())
  const [toggleError, setToggleError] = useState(null)

  const handleOpenEditDialog = async (role) => {
    setEditingRole(role)
    setShowEditDialog(true)
    setEditLoading(true)
    setEditLoadError(null)
    setToggleError(null)
    setEditPermissions([])

    const result = await apiService.fetchPermissionPrivileges(companyUrl, role.id)
    if (!result.success) {
      setEditLoadError(result.error || 'Failed to load role permissions.')
      setEditLoading(false)
      return
    }
    setEditPermissions(Array.isArray(result.data) ? result.data : [])
    setEditLoading(false)
  }

  const handleTogglePrivilege = async (permissionId, privilegeId, isEnabled) => {
    const key = `${permissionId}-${privilegeId}`
    setTogglingKeys(prev => new Set(prev).add(key))
    setToggleError(null)

    const result = await apiService.updatePermissionPrivilege(companyUrl, editingRole.id, {
      permission_id: permissionId,
      privilege_id: privilegeId,
      privilege_id_status: isEnabled ? 0 : 1,
    })

    if (!result.success) {
      setToggleError(result.error || 'Failed to update permission.')
    } else {
      setEditPermissions(prev => prev.map(entry => {
        if (entry.permission_id !== permissionId) return entry
        const currentPrivileges = entry.privileges || []
        const nextPrivileges = isEnabled
          ? currentPrivileges.filter(p => p !== privilegeId)
          : [...currentPrivileges, privilegeId]
        return { ...entry, privileges: nextPrivileges }
      }))
    }

    setTogglingKeys(prev => {
      const next = new Set(prev)
      next.delete(key)
      return next
    })
  }

  return (
    <div className="space-y-6">
      {/* ── Header ── */}
      <PageHeader
        backTo="/platform-setup"
        backLabel="Platform Setup"
        title="Roles Editor"
        subtitle="Define roles and the permissions assigned to them"
        actions={(
          <>
            {isSuperAdmin && (
              <div className="w-full sm:w-64 sm:flex-shrink-0">
                <Select
                  value={selectedCompanyId != null ? String(selectedCompanyId) : ''}
                  onValueChange={handleCompanyChange}
                  disabled={loadingCompanies || companies.length === 0}
                >
                  <SelectTrigger className="h-9 text-sm">
                    <div className="flex items-center gap-2 truncate">
                      <Building2 className="h-3.5 w-3.5 flex-shrink-0 text-slate-500" />
                      <SelectValue placeholder={loadingCompanies ? 'Loading companies…' : 'Select a company'} />
                    </div>
                  </SelectTrigger>
                  <SelectContent>
                    {companies.map(company => (
                      <SelectItem key={company.id} value={String(company.id)}>
                        {company.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <Button
              onClick={handleOpenAddDialog}
              size="sm"
              className="gap-1.5"
            >
              <Plus className="h-4 w-4" />
              Add Role
            </Button>
          </>
        )}
      />

      {/* ── Roles list ── */}
      {loading ? (
        <div className="flex items-center justify-center gap-2 text-slate-500 py-16">
          <Loader2 className="h-5 w-5 animate-spin" />
          <span className="text-sm">Loading roles…</span>
        </div>
      ) : loadError ? (
        <Card className="shadow-sm border-slate-200">
          <CardContent className="p-6 flex items-center gap-2 text-red-500">
            <AlertCircle className="h-4 w-4" />
            <span className="text-sm">{loadError}</span>
          </CardContent>
        </Card>
      ) : roles.length === 0 ? (
        <Card className="shadow-sm border-slate-200">
          <CardContent className="p-10 text-center">
            <Users className="h-8 w-8 text-slate-300 mx-auto mb-3" />
            <p className="text-sm font-medium text-slate-600">No roles yet</p>
            <p className="text-xs text-slate-400 mt-1">Create your first role to get started.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {roles.map((role, index) => (
            <RoleCard
              key={role.id}
              role={role}
              colorKey={ROLE_CARD_COLORS[index % ROLE_CARD_COLORS.length]}
              permissionItemsById={permissionItemsById}
              onClick={() => handleOpenEditDialog(role)}
            />
          ))}
        </div>
      )}

      {/* ── Add Role Dialog ── */}
      <Dialog open={showAddDialog} onOpenChange={(open) => { if (!addRoleSaving) setShowAddDialog(open) }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <div className="flex items-center gap-3 mb-1">
              <div className="p-2.5 bg-purple-100 rounded-xl">
                <ShieldCheck className="h-5 w-5 text-purple-600" />
              </div>
              <DialogTitle className="text-lg font-semibold text-slate-800">
                Add Role
              </DialogTitle>
            </div>
            <DialogDescription className="text-sm text-slate-500 leading-relaxed pt-1">
              Create a new role and assign the permissions it should have access to.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-1.5">
              <label className="block text-sm font-medium text-slate-700">Role Name</label>
              <Input
                value={newRoleName}
                onChange={e => setNewRoleName(e.target.value)}
                placeholder="Regional Manager"
                className="rounded-xl"
                disabled={addRoleSaving}
              />
            </div>

            <div className="space-y-1.5">
              <label className="block text-sm font-medium text-slate-700">Description</label>
              <Textarea
                value={newRoleDescription}
                onChange={e => setNewRoleDescription(e.target.value)}
                placeholder="What this role is responsible for…"
                className="rounded-xl min-h-[72px]"
                disabled={addRoleSaving}
              />
            </div>

            <div className="space-y-1.5">
              <label className="block text-sm font-medium text-slate-700">Role Code</label>
              <Select
                value={newRoleCode}
                onValueChange={setNewRoleCode}
                disabled={addRoleSaving}
              >
                <SelectTrigger className="rounded-xl">
                  <SelectValue placeholder="Select role code…" />
                </SelectTrigger>
                <SelectContent>
                  {ROLE_CODES.map(code => (
                    <SelectItem key={code} value={code}>
                      {code}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <label className="block text-sm font-medium text-slate-700">Permissions</label>
              <PermissionsMultiSelect
                permissionItems={permissionItems}
                selectedIds={newRolePermissionIds}
                onChange={setNewRolePermissionIds}
                disabled={addRoleSaving}
              />
            </div>

            {addRoleError && (
              <div className="flex items-start gap-2.5 bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-700">
                <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                <span>{addRoleError}</span>
              </div>
            )}
          </div>

          <DialogFooter className="gap-2 sm:gap-2 pt-2">
            <Button
              variant="outline"
              className="rounded-xl flex-1 sm:flex-none"
              onClick={() => setShowAddDialog(false)}
              disabled={addRoleSaving}
            >
              Cancel
            </Button>
            <Button
              onClick={handleAddRole}
              disabled={addRoleSaving}
              className="rounded-xl flex-1 sm:flex-none bg-fitness-primary hover:bg-fitness-primary/90 text-primary-foreground gap-1.5"
            >
              {addRoleSaving ? (
                <><Loader2 className="h-4 w-4 animate-spin" /> Creating…</>
              ) : (
                <><Plus className="h-4 w-4" /> Create Role</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Edit Role Permissions Dialog ── */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <div className="flex items-center gap-3 mb-1">
              <div className="p-2.5 bg-fitness-primary/10 rounded-xl">
                <ShieldCheck className="h-5 w-5 text-fitness-primary" />
              </div>
              <DialogTitle className="text-lg font-semibold text-slate-800">
                {editingRole?.display_name || editingRole?.name || 'Role'} Permissions
              </DialogTitle>
            </div>
            <DialogDescription className="text-sm text-slate-500 leading-relaxed pt-1">
              Toggle Read, Write, and Admin access for each permission assigned to this role.
            </DialogDescription>
          </DialogHeader>

          {editLoading ? (
            <div className="flex items-center justify-center gap-2 text-slate-500 py-12">
              <Loader2 className="h-5 w-5 animate-spin" />
              <span className="text-sm">Loading permissions…</span>
            </div>
          ) : editLoadError ? (
            <div className="flex items-start gap-2.5 bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-700">
              <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
              <span>{editLoadError}</span>
            </div>
          ) : editPermissions.length === 0 ? (
            <div className="text-center py-10">
              <KeyRound className="h-7 w-7 text-slate-300 mx-auto mb-2" />
              <p className="text-sm text-slate-500">This role has no permissions assigned yet.</p>
            </div>
          ) : (
            <div className="max-h-[60vh] overflow-y-auto -mx-1 px-1">
              {toggleError && (
                <div className="flex items-start gap-2.5 bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-700 mb-3">
                  <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                  <span>{toggleError}</span>
                </div>
              )}

              <div className="space-y-2">
                {editPermissions.map(entry => {
                  const permission = entry.permissions?.[0]
                  const activePrivileges = entry.privileges || []
                  return (
                    <div
                      key={entry.permission_id}
                      className="flex items-center justify-between gap-4 rounded-xl border border-slate-100 bg-slate-50/60 px-4 py-3"
                    >
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-slate-700">
                          {permission?.display_name || `Permission #${entry.permission_id}`}
                        </p>
                        {permission?.description && (
                          <p className="text-xs text-slate-400 mt-0.5 leading-snug">{permission.description}</p>
                        )}
                      </div>

                      <div className="flex items-center gap-4 flex-shrink-0">
                        {PRIVILEGES.map(({ id, label, icon: Icon }) => {
                          const isEnabled = activePrivileges.includes(id)
                          const isToggling = togglingKeys.has(`${entry.permission_id}-${id}`)
                          return (
                            <div key={id} className="flex flex-col items-center gap-1">
                              <span className="flex items-center gap-1 text-[11px] font-medium text-slate-500">
                                <Icon className="h-3 w-3" />
                                {label}
                              </span>
                              <Switch
                                checked={isEnabled}
                                disabled={isToggling}
                                onCheckedChange={() => handleTogglePrivilege(entry.permission_id, id, isEnabled)}
                              />
                            </div>
                          )
                        })}
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          )}

          <DialogFooter className="pt-2">
            <Button
              variant="outline"
              className="rounded-xl"
              onClick={() => setShowEditDialog(false)}
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default RolesEditor
